import React, { useState, useEffect } from 'react';
import { reserveTicket } from '../services/raffleService';
import { sendTicketNotification } from '../services/notificationService';
import { useSpring, animated } from '@react-spring/web';
import { useTranslation } from '../hooks/useTranslation';
import toast from 'react-hot-toast';

const SimpleTicketModal = ({ ticket, raffle, raffleId, onClose }) => {
  const { t } = useTranslation();
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [showNoteField, setShowNoteField] = useState(false);
  const [specialNote, setSpecialNote] = useState('');
  const [loading, setLoading] = useState(false);

  const modalAnimation = useSpring({
    from: { opacity: 0, transform: 'scale(0.9) translateY(50px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0px)' },
    config: { tension: 300, friction: 25 }
  });

  const overlayAnimation = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 }
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!buyerInfo.name || !buyerInfo.phone) {
      toast.error(t('modal.requiredFields'));
      return;
    }

    setLoading(true);
    
    try {
      const reservationData = {
        ...buyerInfo,
        totalPaid: 0, // Sin pago inicial
        balance: raffle.price, // Todo pendiente
        paymentHistory: [],
        specialNote: showNoteField ? specialNote : '',
        reservedAt: new Date().toISOString()
      };

      console.log('=== DEBUG SimpleTicketModal ===');
      console.log('showNoteField:', showNoteField);
      console.log('specialNote:', specialNote);
      console.log('reservationData:', reservationData);
      console.log('===========================');

      await reserveTicket(raffleId, ticket.id, reservationData);
      await sendTicketNotification(raffleId, ticket.number, buyerInfo.name);
      
      toast.success(
        <div className="flex items-center">
          <span className="text-2xl mr-2">🎉</span>
          <span>{t('modal.ticketReserved', { number: ticket.displayNumbers || ticket.number })}</span>
        </div>,
        { duration: 5000 }
      );
      
      onClose();
    } catch (error) {
      console.error('Error reserving ticket:', error);
      toast.error(t('modal.errorReserving'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <animated.div 
      style={overlayAnimation}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <animated.div 
        style={modalAnimation}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header compacto */}
        <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 p-3 text-white">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="flex items-center justify-between px-2">
            {/* Logo de Google Play con TalonarioRifas */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/google-play-badge.svg" 
                alt="Google Play" 
                className="h-8 mb-1"
              />
              <p className="text-xs font-bold text-gray-700 text-center">TalonarioRifas</p>
            </div>
            
            {/* Información del boleto */}
            <div className="text-center flex-1">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full mb-1">
                <span className="text-lg font-bold">{ticket.displayNumbers || ticket.number}</span>
              </div>
              <h2 className="text-sm font-bold">{t('modal.reserveTicket')}</h2>
              <p className="text-xs text-white/80">{t('common.value')}: {formatCurrency(raffle.price)}</p>
            </div>
            
            {/* Espaciador para balance */}
            <div className="w-16"></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-100px)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Información Personal */}
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                {t('modal.personalInfo')}
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('modal.fullName')} *
                  </label>
                  <input
                    type="text"
                    value={buyerInfo.name}
                    onChange={(e) => setBuyerInfo({...buyerInfo, name: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-all"
                    placeholder={t('placeholders.name')}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('modal.phone')} *
                  </label>
                  <input
                    type="tel"
                    value={buyerInfo.phone}
                    onChange={(e) => setBuyerInfo({...buyerInfo, phone: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-all"
                    placeholder={t('placeholders.phone')}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('modal.email')}
                  </label>
                  <input
                    type="email"
                    value={buyerInfo.email}
                    onChange={(e) => setBuyerInfo({...buyerInfo, email: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-all"
                    placeholder={t('placeholders.email')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('modal.address')}
                  </label>
                  <input
                    type="text"
                    value={buyerInfo.address}
                    onChange={(e) => setBuyerInfo({...buyerInfo, address: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-all"
                    placeholder={t('placeholders.address')}
                  />
                </div>
              </div>
            </div>

            {/* Opción de anotación especial */}
            <div className="border-t pt-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showNoteField}
                  onChange={(e) => setShowNoteField(e.target.checked)}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {t('modal.specialNote')}
                </span>
              </label>
              
              {showNoteField && (
                <div className="mt-3">
                  <textarea
                    value={specialNote}
                    onChange={(e) => setSpecialNote(e.target.value)}
                    placeholder={t('modal.writeNote')}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-all"
                    rows="3"
                  />
                </div>
              )}
            </div>

            {/* Nota informativa */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('modal.confirmNote')}
              </p>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : (
                  t('common.confirm')
                )}
              </button>
            </div>
          </form>
        </div>
      </animated.div>
    </animated.div>
  );
};

export default SimpleTicketModal;